import { audioDebug } from './debug-overlay';

export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private nextStartTime = 0;
  private sources: AudioBufferSourceNode[] = [];
  private _playing = false;
  private _playbackRate = 1.0;
  private _playbackStartTime = 0;
  private _playbackStartOffset = 0;
  private _totalSamples = 0;
  private _sampleRate = 0;
  private analyser: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  // Silent keep-alive: prevents Android from suspending the AudioContext between
  // messages. Without this, the context goes silent → Android suspends it →
  // next resume() triggers the OS audio session acquisition sound (the beep).
  private keepAliveOsc: OscillatorNode | null = null;
  private keepAliveGain: GainNode | null = null;
  get playbackRate() { return this._playbackRate; }
  set playbackRate(v: number) { this._playbackRate = Math.max(0.25, Math.min(4.0, v)); }

  get isPlaying() {
    return this._playing;
  }

  /** Create/resume AudioContext — call during a user gesture to satisfy autoplay policy */
  prime() {
    const wasMissing = !this.ctx || this.ctx.state === "closed";
    if (wasMissing) {
      this.ctx = new AudioContext();
      audioDebug('prime', `new AudioContext created, state=${this.ctx.state}`);
    }
    if (this.ctx!.state === "suspended") {
      audioDebug('prime', 'resuming suspended ctx');
      this.ctx!.resume();
    }
    audioDebug('prime', `done, state=${this.ctx!.state}`);
    // Keep Android audio session alive between messages
    this.ensureKeepAlive();
    if (!this.analyser && this.ctx) {
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;
      this.analyser.connect(this.ctx.destination);
      this.analyserData = new Uint8Array(this.analyser.frequencyBinCount);
    }
  }

  /**
   * Start a silent oscillator to keep the AudioContext alive.
   * Android Chrome suspends AudioContext when no audio is playing.
   * A zero-gain oscillator keeps the session acquired so the next
   * real audio plays instantly without re-acquisition (no beep).
   */
  private ensureKeepAlive() {
    if (this.keepAliveOsc || !this.ctx) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      gain.gain.value = 0; // completely silent — no CPU cost for actual output
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      this.keepAliveOsc = osc;
      this.keepAliveGain = gain;
      console.log("[HEALTH:keep-alive] silent oscillator started");
    } catch (e) {
      console.warn("[HEALTH:keep-alive] failed to start:", e);
    }
  }

  enqueue(samples: Float32Array, sampleRate: number) {
    // prime() is the single creation point — if context is missing, prime it as fallback
    if (!this.ctx || this.ctx.state === "closed") {
      this.prime();
    }
    if (!this.ctx) {
      console.error("[audio] No AudioContext available, dropping chunk");
      return;
    }

    // Resume if suspended — should rarely fire with keep-alive running
    if (this.ctx.state === "suspended") {
      audioDebug('enqueue', 'WARN: resuming suspended ctx — keep-alive failed?');
      this.ctx.resume();
    }

    audioDebug('chunk', `${samples.length} samples @ ${sampleRate}Hz, ctx=${this.ctx.state}`);
    const buffer = this.ctx.createBuffer(1, samples.length, sampleRate);
    // Cast: samples arrive via postMessage transfer — always a plain ArrayBuffer at runtime
    buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this.playbackRate;

    // Per-source gain node with 2ms fade-in/out to prevent clicks.
    // Without this, non-zero sample boundaries between consecutive buffers
    // cause the speaker cone to jump — audible as a click/beep on phone speakers.
    const gain = this.ctx.createGain();
    source.connect(gain);
    gain.connect(this.analyser ?? this.ctx.destination);

    const startAt = Math.max(this.nextStartTime, this.ctx.currentTime);
    const duration = buffer.duration / this.playbackRate;
    const RAMP = 0.002; // 2ms — below auditory perception threshold

    // Fade in: silence → full volume over 2ms
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(1, startAt + RAMP);

    // Fade out: full volume → silence over 2ms (skip if chunk too short)
    if (duration > RAMP * 4) {
      gain.gain.setValueAtTime(1, startAt + duration - RAMP);
      gain.gain.linearRampToValueAtTime(0, startAt + duration);
    }

    source.start(startAt);
    this.nextStartTime = startAt + duration;

    this._playing = true;
    this.sources.push(source);

    source.onended = () => {
      this.sources = this.sources.filter((s) => s !== source);
      if (this.sources.length === 0) {
        this._playing = false;
      }
    };
  }

  playFromBuffer(samples: Float32Array, sampleRate: number, offsetSamples = 0) {
    this.stop();
    this.prime();
    if (!this.ctx) return;

    offsetSamples = Math.max(0, Math.min(offsetSamples, samples.length - 1));

    const buffer = this.ctx.createBuffer(1, samples.length, sampleRate);
    buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this._playbackRate;

    // Fade-in to prevent click from non-zero first sample
    const gain = this.ctx.createGain();
    source.connect(gain);
    gain.connect(this.analyser ?? this.ctx.destination);
    const now = this.ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + 0.002);

    source.start(0, offsetSamples / sampleRate);

    this._playbackStartTime = this.ctx.currentTime;
    this._playbackStartOffset = offsetSamples;
    this._totalSamples = samples.length;
    this._sampleRate = sampleRate;
    this._playing = true;
    this.sources.push(source);

    source.onended = () => {
      this.sources = this.sources.filter((s) => s !== source);
      if (this.sources.length === 0) {
        this._playing = false;
      }
    };
  }

  getPlaybackProgress(): number {
    if (!this._playing || !this.ctx || this._totalSamples === 0) return 0;
    const elapsed = (this.ctx.currentTime - this._playbackStartTime) * this._playbackRate;
    const position = elapsed * this._sampleRate + this._playbackStartOffset;
    return Math.min(1, position / this._totalSamples);
  }

  /** Stop playback but keep AudioContext alive for reuse */
  stop() {
    audioDebug('stop', `sources=${this.sources.length} ctx=${this.ctx?.state ?? 'null'}`);
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // Already stopped or never started — ignore
      }
    }
    this.sources = [];
    this._playing = false;
    this.nextStartTime = 0;
    this._playbackStartTime = 0;
    this._playbackStartOffset = 0;
    this._totalSamples = 0;
    this._sampleRate = 0;
    // Don't close context — reuse it to avoid autoplay policy issues
  }

  getAudioLevel(): number {
    if (!this.analyser || !this.analyserData || !this._playing) return 0;
    // TS 5.7+ Uint8Array generic — cast to satisfy WebAudio's ArrayBuffer-backed expectation.
    this.analyser.getByteFrequencyData(this.analyserData as Uint8Array<ArrayBuffer>);
    let sum = 0;
    for (let i = 0; i < this.analyserData.length; i++) {
      sum += this.analyserData[i];
    }
    return sum / (this.analyserData.length * 255);
  }

  /** True when AudioContext exists and is in 'running' state */
  isContextRunning(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Current AudioContext state for diagnostics */
  ctxState(): string {
    return this.ctx?.state ?? 'null';
  }

  /** Full teardown — close AudioContext. Use on unmount only. */
  destroy() {
    this.stop();
    // Stop keep-alive oscillator before closing context
    if (this.keepAliveOsc) {
      try { this.keepAliveOsc.stop(); } catch { /* already stopped */ }
      this.keepAliveOsc.disconnect();
      this.keepAliveOsc = null;
    }
    if (this.keepAliveGain) {
      this.keepAliveGain.disconnect();
      this.keepAliveGain = null;
    }
    this.analyser = null;
    this.analyserData = null;
    if (this.ctx) {
      this.ctx.close().catch(() => {});
      this.ctx = null;
    }
  }
}
